/**
 * Department multi-select for shell sticky bar (incidents, team, compliance filters).
 * Empty selection = ทั้งหมด (no narrowing). Uses searchable checkbox dropdown (no native `<select multiple>`).
 * Options are grouped by LOF with Firestore `lof_name` (and static fallbacks) as section headings.
 */
import React from "react";

import { useLofDisplayNameMap } from "../hooks/useLofDisplayNameMap";
import { buildLofGroupedDepartmentOptions } from "../lib/departmentUi";
import {
  MultiSelectDropdown,
  type MultiSelectOption,
  type MultiSelectOptionGroup,
} from "./ui/MultiSelectDropdown";

export type TeamDepartmentFilterOption = { id: string; name: string; lofId?: string };

type TeamDepartmentFilterBarProps = {
  options: TeamDepartmentFilterOption[];
  /** Empty = ทั้งหมด (no narrowing). */
  selectedIds: string[];
  onChange: (next: string[]) => void;
  /** Prefix kept for API compatibility; MultiSelectDropdown generates its own ids from `label`. */
  idPrefix?: string;
};

function normalizeSelectedIds(selectedIds: string[], allowedIds: string[]): string[] {
  if (!selectedIds.length) return [];
  // Options can be briefly empty while departments reload; do not clear user selection on refetch.
  if (!allowedIds.length) return [...selectedIds];

  const allowed = new Set(allowedIds);
  const next = Array.from(new Set(selectedIds.filter((id) => allowed.has(id))));
  return next.length === allowedIds.length ? [] : next;
}

function sameIdSet(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;
  const rightSet = new Set(right);
  return left.every((id) => rightSet.has(id));
}

export function TeamDepartmentFilterBar({
  options,
  selectedIds,
  onChange,
  idPrefix: _idPrefix,
}: TeamDepartmentFilterBarProps) {
  const lofIdToName = useLofDisplayNameMap();

  const { flatOptions, optionGroups } = React.useMemo(() => {
    const built = buildLofGroupedDepartmentOptions(options, lofIdToName);
    return {
      flatOptions: built.flatOptions as MultiSelectOption[],
      optionGroups: built.optionGroups as MultiSelectOptionGroup[],
    };
  }, [options, lofIdToName]);
  const optionValues = React.useMemo(() => flatOptions.map((o) => o.value), [flatOptions]);
  const previousOptionValuesRef = React.useRef<string[]>(optionValues);
  const normalizedSelectedIds = React.useMemo(
    () => normalizeSelectedIds(selectedIds, optionValues),
    [selectedIds, optionValues],
  );

  React.useEffect(() => {
    const previousOptionValues = previousOptionValuesRef.current;
    previousOptionValuesRef.current = optionValues;

    if (
      selectedIds.length > 0 &&
      !sameIdSet(previousOptionValues, optionValues) &&
      sameIdSet(selectedIds, previousOptionValues)
    ) {
      onChange([]);
      return;
    }

    if (sameIdSet(normalizedSelectedIds, selectedIds)) return;
    onChange(normalizedSelectedIds);
  }, [normalizedSelectedIds, onChange, optionValues, selectedIds]);

  if (options.length <= 1) return null;

  return (
    <MultiSelectDropdown
      label="กรองหน่วยงาน"
      multiple
      emptyMeansAll
      allLabel="ทั้งหมด"
      value={normalizedSelectedIds}
      options={flatOptions}
      optionGroups={optionGroups}
      onChange={(next) => {
        const sanitizedNext = normalizeSelectedIds(next, optionValues);
        if (sanitizedNext.length === 0) {
          onChange([]);
          return;
        }
        onChange(sanitizedNext);
      }}
      searchable
      showSelectAll
      showClear
      className="max-w-full text-sm [&_button]:min-h-[2.25rem] [&_button]:text-sm [&_button]:px-3 [&_button]:py-1.5"
    />
  );
}
